/**
 * SpotTruckPage.jsx — v1.0.0 Gate 5.5 Item 1
 *
 * Lightweight landing that helps a user log a truck spot.
 * User picks a truck from their follow list or searches, captures current
 * location, then POSTs to /api/trucks/spot (existing). Gamification awards
 * +10 via the truck_spotted rule seeded in Gate 5.5.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Truck, MapPin, Search,
} from "lucide-react";
import { API, axios } from "@/api";
import { useTheme } from "@/contexts/ThemeContext";
import { BottomNav } from "@/components/shared/Navigation";

export default function SpotTruckPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [coords, setCoords] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Pull user's followed food trucks as initial list
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/trucks/followed`).catch(() => ({ data: [] }));
        setResults((res.data || []).slice(0, 10));
      } catch {}
    })();
  }, []);

  // Search on query change
  useEffect(() => {
    if (!query || query.length < 2) return;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(
          `${API}/birthday-offers?search=${encodeURIComponent(query)}&listing_type=food_truck`
        );
        setResults((res.data?.offers || res.data || []).slice(0, 10));
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const captureLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Location not available");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => toast.error("Couldn't get your location"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handleSubmit = async () => {
    if (!selected) return toast.error("Select a truck first");
    if (!coords) return toast.error("Tap Use my location first");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/trucks/spot`, {
        business_id: selected.business_id,
        lat: coords.lat,
        lng: coords.lng,
        spot_type: "on_foot",
      });
      if (res.data?.success || res.data?.spot_id) {
        toast.success("Spot logged — thanks!");
        setTimeout(() => navigate("/activity"), 1200);
      } else {
        toast.success("Spot submitted");
        setTimeout(() => navigate("/activity"), 1200);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't log spot";
      toast.error(typeof msg === "string" ? msg : "Couldn't log spot");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pb-24" style={{ background: theme.pageBg }} data-testid="spot-truck-page">
      <div className="px-5 pt-6 pb-4" style={{ background: theme.headerGradient }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-xl bg-white/10" data-testid="spot-truck-back-btn">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white" data-testid="spot-truck-title">Spot a Food Truck</h1>
            <p className="text-white/70 text-xs mt-0.5">+10 points. Help others find it in real time.</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search a food truck…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
            data-testid="spot-truck-search"
          />
        </div>

        {/* Results */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="spot-truck-results">
          {searching && (
            <div className="flex justify-center py-5"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          )}
          {results.length === 0 && !searching ? (
            <p className="px-4 py-5 text-xs text-slate-400 text-center">Search to find a truck.</p>
          ) : (
            results.map((r) => {
              const active = selected?.business_id === r.business_id;
              return (
                <button
                  key={r.business_id}
                  onClick={() => setSelected(r)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-slate-50 last:border-0 transition-colors ${
                    active ? "bg-sky-50" : "hover:bg-slate-50"
                  }`}
                  data-testid={`spot-truck-result-${r.business_id}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Truck className="w-4 h-4 text-orange-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-800 truncate">{r.name}</p>
                    <p className="text-[10px] text-slate-400">{r.category || "Food truck"}</p>
                  </div>
                  {active && <span className="text-[10px] font-bold text-sky-600">Selected</span>}
                </button>
              );
            })
          )}
        </div>

        {/* Location */}
        <button
          onClick={captureLocation}
          className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold ${
            coords ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-700"
          }`}
          data-testid="spot-truck-location-btn"
        >
          <MapPin className="w-4 h-4" />
          {coords ? `Location captured (${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)})` : "Use my current location"}
        </button>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!selected || !coords || submitting}
          className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="spot-truck-submit-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Submit Spot (+10 pts)"}
        </button>
      </div>

      <BottomNav />
    </div>
  );
}
